import { TextArea } from '@wonderschool/common-base-ui';
import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-restricted-imports
import { Form, Segment } from 'semantic-ui-react';
import { InvitationRecipientType } from '../../../types';
import DocumentsView from '../DocumentsView';
import InvitationMessageView from '../InvitationMessageView';
import InvitationRecipientsView from '../InvitationRecipientsView';
import NewEnrollmentInvoicesView from '../NewEnrollmentInvoicesView';
import ScheduleView from '../ScheduleView';
import StudentView from '../StudentView';

type StudentSummaryProps = {
  enrollmentForm: UseFormReturn<any>;
  studentFromForm: any;
  invitationRecipients: InvitationRecipientType[];
  scheduledDays: any[];
  extraNotes: string;
  setExtraNotes: (notes: string) => void;
};

const StudentSummary: React.FC<StudentSummaryProps> = ({
  enrollmentForm,
  studentFromForm,
  invitationRecipients,
  scheduledDays,
  extraNotes,
  setExtraNotes,
}) => {
  const { t } = useTranslation();

  const tuitionAndFees = useMemo(() => {
    const enrollmentData = enrollmentForm.getValues();
    return enrollmentData.tuitionAndFees || {};
  }, [enrollmentForm]);

  const oneTimeFees = useMemo(() => {
    const enrollmentData = enrollmentForm.getValues();
    return enrollmentData.oneTimeFees || [];
  }, [enrollmentForm]);

  const shouldShowEnrollmentInvoices = useMemo(() => {
    const hasTuition = tuitionAndFees && tuitionAndFees.tuition && Object.keys(tuitionAndFees.tuition).length > 0;
    const hasOneTimeFees = oneTimeFees && oneTimeFees.length > 0;
    return hasTuition || hasOneTimeFees;
  }, [tuitionAndFees, oneTimeFees]);

  return (
    <>
      <Segment basic clearing>
        <h1 className="text-3xl" data-testid="dig-enroll-invitation">
          {t('enrollments.pageTitleSummary')}
        </h1>
        <Segment>
          <StudentView student={studentFromForm} />
        </Segment>
        <Segment>
          <InvitationRecipientsView invitationRecipients={invitationRecipients} showEditIcon={false} />
        </Segment>
        <Segment>
          <DocumentsView documents={enrollmentForm.getValues('documents')} showEditIcon={false} />
        </Segment>
        <Segment>
          <ScheduleView scheduledDays={scheduledDays} showEditIcon={false} />
        </Segment>
        {shouldShowEnrollmentInvoices && (
          <Segment>
            <NewEnrollmentInvoicesView tuition={tuitionAndFees.tuition} fees={oneTimeFees} />
          </Segment>
        )}
        <Segment>
          <InvitationMessageView />
          {renderExtraNotesForm()}
        </Segment>
      </Segment>
    </>
  );

  function renderExtraNotesForm() {
    return (
      <Form>
        <p data-testid="extra-notes">
          <strong>{t('enrollments.enrollmentInfoExtraNotesLabel')}</strong>
        </p>
        <TextArea
          value={extraNotes}
          onChange={(e) => setExtraNotes(e.target.value)}
          data-testid="extra-notes-textarea"
        />
      </Form>
    );
  }
};

export default StudentSummary;
